import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CountryEntry } from '@/domain';
import type { PhoneNumbers } from '@/application/ports/PhoneBook';
import { strings } from '@/shared/strings';
import { ChevronIcon } from '@/ui/icons';
import { Combobox, type ComboboxOption } from '@/ui/primitives/Combobox';
import { modalStyles } from '@/ui/primitives/Modal';
import { caretAfterFormat, countryOfEntry, phoneTyped, type PhoneEntry } from './phoneEntry';
import styles from './PhoneField.module.css';

export interface PhoneFieldProps {
  /** The id of the box the number is typed in, which the form's label names. */
  id: string;
  value: PhoneEntry;
  onChange: (next: PhoneEntry) => void;
  /** The telephone library, or undefined for the moment it takes to arrive. */
  book: PhoneNumbers | undefined;
  /** The country list, for the names beside the codes, or undefined until it arrives. */
  countries: readonly CountryEntry[] | undefined;
  invalid?: boolean;
  /** The id of the line that says what is wrong, when there is one. */
  describedBy?: string;
  /** Where the form finds the box, to put the focus on it. */
  inputRef?: (element: HTMLInputElement | null) => void;
}

/**
 * A telephone number of any country: its calling code in a selector, and the
 * rest in a box. The request of 6 October 2026 (fifth), section 2: a
 * searchable country code selector that starts on +971, the number formatted
 * as it is typed and checked by its country's rules, kept as E.164.
 *
 * The selector is a button that holds the code. Pressing it opens a search
 * under the field, of every country by its name, its ISO code or its calling
 * code, on the shared combobox; choosing one puts the code on the button and
 * the focus back in the number. A number typed or pasted whole, with a plus
 * and its own code, moves the selector to its country by itself.
 *
 * The library that knows the rules is large and loaded only when a form
 * needs it. Until it is there the field is the same two controls and a plain
 * box: what is typed is kept as typed, and the list of codes says it is
 * loading. Nothing moves or changes when the library arrives; from then on
 * each keystroke is formatted, and the line under the box says how a number
 * that has become valid will be kept.
 */
export function PhoneField({
  id,
  value,
  onChange,
  book,
  countries,
  invalid = false,
  describedBy,
  inputRef,
}: PhoneFieldProps) {
  const panelId = useId();
  const savedId = useId();
  const [choosing, setChoosing] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLInputElement | null>(null);
  // Where the caret belongs once the number just typed has been formatted and drawn.
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const at = caret.current;
    caret.current = null;
    if (at !== null && document.activeElement === box.current) {
      box.current?.setSelectionRange(at, at);
    }
  });

  /*
   * Every country that has a calling code, by name. A code the country list
   * has no name for (Kosovo, Ascension, Tristan da Cunha) is listed under its
   * letters, after the names.
   */
  const options = useMemo<ComboboxOption[]>(() => {
    if (book === undefined || countries === undefined) return [];
    const names = new Map(countries.map((country) => [country.code, country.name]));

    return book
      .countries()
      .map((each) => ({
        key: each.code,
        label: names.get(each.code) ?? each.code,
        detail: `+${each.callingCode}`,
        alsoFoundBy: [each.code, `+${each.callingCode}`, each.callingCode],
        named: names.has(each.code),
      }))
      .sort((a, b) => Number(b.named) - Number(a.named) || a.label.localeCompare(b.label));
  }, [book, countries]);

  function typed(text: string, at: number | null) {
    if (book === undefined) {
      onChange({ ...value, national: text });
      return;
    }

    // A whole number with its own country code: the selector follows it.
    const whole = text.trim().startsWith('+') ? book.split(text) : null;
    if (whole !== null) {
      onChange({
        country: whole.country,
        code: book.callingCode(whole.country) ?? value.code,
        national: whole.national,
      });
      return;
    }

    const formatted = book.typed(text, countryOfEntry(value, book)).formatted;
    caret.current = at === null ? null : caretAfterFormat(text, at, formatted);
    onChange({ ...value, national: formatted });
  }

  function chosen(option: ComboboxOption) {
    setChoosing(false);
    if (book !== undefined) {
      const code = book.callingCode(option.key) ?? value.code;
      // The number is written again as the country now chosen writes one.
      const national =
        value.national.trim() === '' ? '' : book.typed(value.national, option.key).formatted;
      onChange({ country: option.key, code, national });
    }
    box.current?.focus();
  }

  const read = phoneTyped(value, book);
  const code = `+${value.code}`;
  const described = [describedBy ?? '', read?.display != null ? savedId : '']
    .filter((each) => each !== '')
    .join(' ');

  return (
    <div className={styles.phone}>
      <div className={styles.row}>
        <button
          ref={button}
          type="button"
          className={styles.code}
          aria-label={strings.phone.codeButton(code)}
          aria-expanded={choosing}
          aria-controls={choosing ? panelId : undefined}
          // The search under the field closes when it loses the focus: this press is not that.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setChoosing(!choosing)}
        >
          <span>{code}</span>
          <ChevronIcon />
        </button>
        <input
          ref={(element) => {
            box.current = element;
            inputRef?.(element);
          }}
          id={id}
          className={`${modalStyles.input} ${styles.number}`}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={value.national}
          aria-invalid={invalid || undefined}
          aria-describedby={described === '' ? undefined : described}
          onChange={(event) => typed(event.target.value, event.target.selectionStart)}
        />
      </div>

      {choosing && (
        <div ref={panel} id={panelId} className={styles.panel}>
          <Combobox
            label={strings.phone.codeLabel}
            value=""
            options={options}
            loading={options.length === 0}
            placeholder={strings.phone.codePlaceholder}
            words={strings.phone.list}
            focusOnMount
            onChoose={chosen}
            onDismiss={() => {
              // Put away with Escape, the focus goes back to the button; left for another field, it stays there.
              const escaped = panel.current?.contains(document.activeElement) === true;
              setChoosing(false);
              if (escaped) button.current?.focus();
            }}
          />
        </div>
      )}

      {/* Always there, at the height of its line, so nothing under the field moves as a number becomes valid. */}
      <span id={savedId} className={`${modalStyles.hint} ${styles.saved}`}>
        {read?.display != null ? strings.phone.savedAs(read.display) : ''}
      </span>
    </div>
  );
}
